<script setup lang="ts">
/*
  The Notitia mark, with the app's draw-in timing. Every place the product signs its
  own name goes through here — the rail, the landing top bar, the auth card and the
  hero — so they all draw the same way and a change to the timing is one edit.

  The behaviour is livereader.com's `BrandMark`, for the same reasons:

  - Habitus draws the moment the mark mounts and finishes in 700 ms. That reads well
    as chrome and too fast as the page's opening, where the mark being written stroke
    by stroke is the point. So an ANIMATED mark waits `DELAY_MS`, then draws over
    `DURATION_MS`.
  - The delay uses HabLogo's own `show` prop: mounted with `show` false it rewinds to
    nothing, and flipping it true runs the same draw-in it would have run on mount.
  - Reduced motion skips the delay as well as the animation. HabLogo already skips the
    draw, and a mark that sat invisible for half a second and then popped in would be
    worse than either.

  CHROME PASSES `animate="false"` — the rail and the top bar stay mounted across
  navigation, and HabLogo's docs name exactly that case: a mark that redraws on every
  navigation is noise.

  Colour is `currentColor` unless given, so the mark takes the ink of wherever it
  sits: white on the rail, the page's ink in the top bar.
*/
import { onBeforeUnmount, onMounted, ref } from "vue";
import { HabLogo } from "@livereader/habitus-vue";

const props = withDefaults(
	defineProps<{
		/** Edge length in pixels. The mark is square. */
		size?: number;
		/** Draw the mark in on mount. Off for chrome that stays mounted. */
		animate?: boolean;
		/** Colour of the first stroke; the second follows it unless `accent` is set. */
		color?: string;
		accent?: string;
		/**
		 * The word beside the mark, making it a lock-up. When given, the mark is
		 * decorative — the word already says it.
		 */
		wordmark?: string;
		/**
		 * Accessible name for a mark that stands alone. Omit it where something adjacent
		 * already names the product; a name announced twice is worse than once.
		 */
		label?: string;
	}>(),
	{ size: 32, animate: true, color: undefined, accent: undefined, wordmark: "", label: "" },
);

/** How long the page settles before the mark starts drawing. */
const DELAY_MS = 500;
/** Long enough to follow the stroke; HabLogo starts the second 30% into the first. */
const DURATION_MS = 1600;

const shown = ref(!props.animate);
let timer: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
	if (shown.value) return;
	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		shown.value = true;
		return;
	}
	timer = setTimeout(() => (shown.value = true), DELAY_MS);
});

onBeforeUnmount(() => clearTimeout(timer));
</script>

<template>
	<span
		v-if="wordmark"
		class="inline-flex items-center gap-3"
	>
		<HabLogo
			name="notitia"
			:size="size"
			:color="color"
			:accent="accent"
			:show="shown"
			:animate="animate"
			:duration="DURATION_MS"
			class="shrink-0"
		/>
		<span class="text-md font-medium tracking-tight">{{ wordmark }}</span>
	</span>
	<HabLogo
		v-else
		name="notitia"
		:size="size"
		:color="color"
		:accent="accent"
		:show="shown"
		:animate="animate"
		:duration="DURATION_MS"
		:label="label || undefined"
	/>
</template>
